import { etagFromRow, get, getVersioned, post, postVersioned, seg } from '../client';
import type { ListParams, Page } from '../types/common';
import type {
  Appointment, AppointmentListParams, AppointmentSeries, AvailabilitySearchRequest, BookAppointmentRequest, BookSeriesRequest, CancelAppointmentRequest,
  CreateFlowEventRequest, CreateWaitlistRequest, FlowEvent, HoldSlotRequest, ReadinessItem, RescheduleAppointmentRequest, Resource, ScheduleTemplate, Slot, SlotHold,
  VisitType, WaitlistEntry,
} from '../types/scheduling';

const cmd = (id: string, c: string) => `/appointments/${seg(id)}:${c}`;
type Versioned = { id: string; rowVersion?: number | string };
const idOf = (x: string | Versioned) => (typeof x === 'string' ? x : x.id);
/** Caller's ETag, else `W/"<rowVersion>"` from the item. */
const tagOf = (x: string | Versioned, etag?: string | null) => etag ?? (typeof x === 'string' ? null : etagFromRow(x.rowVersion));

/** Waitlist status filter: the UI's "all" means no filter (R-037 — the backend matches status literally). */
export const waitlistStatusParam = (status?: string) => (!status || status === 'all' ? undefined : status);

export interface RoomingClaim {
  id: string; appointmentId: string; rowVersion: number; start: string; providerName: string;
  patient: { id: string; display: string; dob: string };
}

export const schedulingApi = {
  roomingClaims: (locationId: string, patientId?: string) => get<{ items: RoomingClaim[] }>('/rooming-claims', { locationId, patientId }),
  claimRooming: (visit: RoomingClaim, key?: string) => post(`/encounters/${seg(visit.id)}:claim-rooming`, {}, { idempotent: key ?? true, ifMatch: etagFromRow(visit.rowVersion) }),
  /** Tenant-configured catalog, including all cursor pages; never substitute hardcoded live choices. */
  visitTypes: async (): Promise<VisitType[]> => {
    const items: VisitType[] = [];
    let cursor: string | undefined;
    do {
      const page = await get<Page<VisitType>>('/visit-types', { limit: 100, cursor });
      items.push(...page.items);
      cursor = page.nextCursor ?? undefined;
    } while (cursor);
    return items.sort((a, b) => a.display.localeCompare(b.display));
  },
  resources: (p: ListParams & { kind?: Resource['kind']; locationId?: string } = {}) => get<Page<Resource>>('/resources', { limit: 100, ...p }),
  templates: (p: ListParams & { resourceId?: string } = {}) => get<Page<ScheduleTemplate>>('/schedule-templates', { ...p }),
  /** Paginated: pass `cursor` from the previous page to continue (default 100 per page). */
  searchAvailability: (body: AvailabilitySearchRequest, p: ListParams = {}) =>
    post<Page<Slot>>('/availability:search', body, { query: { limit: 100, ...p } }),
  /**
   * Atomic hold with expiry (SCH-002). `visitType` (code) sets the held duration. `opts.overbook` + `opts.reason`
   * places an authorized overbook (needs schedule.overbook; the reason is recorded).
   */
  hold: (slotId: string, patientId?: string, visitType?: string, opts: Pick<HoldSlotRequest, 'overbook' | 'reason'> = {}) => {
    const body: HoldSlotRequest = { patientId, visitType };
    if (opts.overbook) Object.assign(body, { overbook: true, reason: opts.reason });
    return post<SlotHold>(`/slots/${seg(slotId)}:hold`, body, { idempotent: true });
  },
  /** If-Match: the hold's ETag / W/"rowVersion" (R-005). */
  releaseHold: (hold: string | SlotHold, etag?: string | null) =>
    post<SlotHold>(`/slot-holds/${seg(idOf(hold))}:release`, {}, { idempotent: true, ifMatch: tagOf(hold, etag) }),
  appointments: (p: AppointmentListParams) => get<Page<Appointment>>('/appointments', { limit: 100, ...p }),
  book: (body: BookAppointmentRequest) => post<Appointment>('/appointments', body, { idempotent: true }),
  /** Recurring series at a local wall-clock time; DST-safe on the server (R-047). */
  bookSeries: (body: BookSeriesRequest) => postVersioned<AppointmentSeries>('/appointment-series', body, { idempotent: true }),
  appointment: (id: string) => getVersioned<Appointment>(`/appointments/${seg(id)}`),
  confirm: (id: string, etag: string | null) => post<Appointment>(cmd(id, 'confirm'), {}, { ifMatch: etag, idempotent: true }),
  arrive: (id: string, etag: string | null) => post<Appointment>(cmd(id, 'arrive'), {}, { ifMatch: etag, idempotent: true }),
  undoArrival: (id: string, reason: string, etag: string | null) => post<Appointment>(cmd(id, 'undo-arrival'), { reason }, { ifMatch: etag, idempotent: true }),
  cancel: (id: string, body: CancelAppointmentRequest, etag: string | null) => post<Appointment>(cmd(id, 'cancel'), body, { ifMatch: etag, idempotent: true }),
  noShow: (id: string, etag: string | null) => post<Appointment>(cmd(id, 'no-show'), {}, { ifMatch: etag, idempotent: true }),
  /** Reschedule by replacement: creates a new linked appointment from a hold. */
  reschedule: (id: string, body: RescheduleAppointmentRequest, etag: string | null) =>
    post<Appointment>(cmd(id, 'reschedule'), body, { ifMatch: etag, idempotent: true }),
  checkOut: (id: string, etag: string | null) => post<Appointment>(cmd(id, 'check-out'), {}, { ifMatch: etag, idempotent: true }),
  /** status "all" (or none) → no filter is sent. */
  waitlist: (p: ListParams & { status?: string; patientId?: string } = {}) =>
    get<Page<WaitlistEntry>>('/waitlist', { ...p, status: waitlistStatusParam(p.status) }),
  waitlistEntry: (id: string) => getVersioned<WaitlistEntry>(`/waitlist/${seg(id)}`),
  addToWaitlist: (body: CreateWaitlistRequest) => post<WaitlistEntry>('/waitlist', body, { idempotent: true }),
  /** Remove a waiting entry (reason required; audited). If-Match from the entry's rowVersion. */
  removeFromWaitlist: (entry: string | WaitlistEntry, reason: string, etag?: string | null) =>
    post<WaitlistEntry>(`/waitlist/${seg(idOf(entry))}:remove`, { reason }, { idempotent: true, ifMatch: tagOf(entry, etag) }),
  readiness: (appointmentId: string) => get<Page<ReadinessItem>>(`/appointments/${seg(appointmentId)}/readiness`),
  /** If-Match from the readiness item's rowVersion. */
  completeRequirement: (item: string | ReadinessItem, note?: string, etag?: string | null) =>
    post<ReadinessItem>(`/previsit-requirements/${seg(idOf(item))}:complete`, note ? { note } : {}, { idempotent: true, ifMatch: tagOf(item, etag) }),
  overrideRequirement: (item: string | ReadinessItem, reason: string, etag?: string | null) =>
    post<ReadinessItem>(`/previsit-requirements/${seg(idOf(item))}:override`, { reason }, { idempotent: true, ifMatch: tagOf(item, etag) }),
  flowEvents: (encounterId: string) => get<Page<FlowEvent>>(`/encounters/${seg(encounterId)}/flow-events`),
  /**
   * Flow events version the ENCOUNTER: If-Match is the encounter ETag. When the caller has none, the current one is
   * read first (GET /encounters/{id}). Returns the event and the encounter's new ETag for chaining.
   */
  addFlowEventVersioned: async (encounterId: string, body: CreateFlowEventRequest, etag?: string | null) => {
    const ifMatch = etag ?? (await getVersioned<unknown>(`/encounters/${seg(encounterId)}`)).etag;
    return postVersioned<FlowEvent>(`/encounters/${seg(encounterId)}/flow-events`, body, { idempotent: true, ifMatch });
  },
  addFlowEvent: async (encounterId: string, body: CreateFlowEventRequest, etag?: string | null) =>
    (await schedulingApi.addFlowEventVersioned(encounterId, body, etag)).data,
};
